import { z } from 'zod';
import { UserRole, schemas } from '@kreart/shared';

/**
 * Parse/normalise TanStack Router search params for the users list.
 *
 * Contract:
 *   - Returns a normalised object with only non-default keys present.
 *   - Strips `includeDeleted=true` when the actor is not an admin (defence-in-depth
 *     against bookmarked URLs hitting a 403 from the API).
 *   - Ignores any unknown keys.
 */
export interface UsersListSearch {
  page?: number;
  pageSize?: number;
  search?: string;
  role?: UserRole;
  active?: boolean;
  includeDeleted?: boolean;
}

const boolLoose = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => (typeof v === 'string' ? v === 'true' : v));

const raw = z
  .object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().trim().min(1).max(100).optional(),
    role: z.nativeEnum(UserRole).optional(),
    active: boolLoose.optional(),
    includeDeleted: boolLoose.optional(),
  })
  .passthrough();

export function parseUsersListSearch(
  input: Record<string, unknown>,
  opts: { isAdmin: boolean },
): UsersListSearch {
  const parsed = raw.safeParse(input);
  if (!parsed.success) return {};
  const v = parsed.data;
  const out: UsersListSearch = {};
  if (v.page !== undefined && v.page !== 1) out.page = v.page;
  if (v.pageSize !== undefined && v.pageSize !== 25) out.pageSize = v.pageSize;
  if (v.search) out.search = v.search;
  if (v.role) out.role = v.role;
  if (v.active !== undefined) out.active = v.active;
  if (v.includeDeleted && opts.isAdmin) out.includeDeleted = true;
  return out;
}

/** Merge a UsersListSearch with full defaults for API calls. */
export function toListQuery(search: UsersListSearch) {
  return schemas.listUsersQuery.parse({
    page: search.page ?? 1,
    pageSize: search.pageSize ?? 25,
    search: search.search,
    role: search.role,
    active: search.active,
    includeDeleted: search.includeDeleted ?? false,
  });
}
